import { ELDER, FERRYMAN, GRAVEDIGGER, HUNTER, SCHOLAR } from './swamp-village.js';
const CARAVAN_MASTER = { mob: 'caravan-master', biome: 'desert' };
/**
 * The swamp's chain, second half (ADR 0047): the scholar of the Ruines
 * Noyées and the lizardmen's rite, the Lizard Shaman lured out, the view
 * from the Tertre Noyé, the Bog King at large, then the whole village
 * mustered against the troll of the Bourbier, and south to Sablerouge.
 */
export const SWAMP_DEPTHS_QUESTS = [
    {
        id: 'swamp-ruins',
        name: 'Les Ruines Noyées',
        biome: 'swamp',
        level: 11,
        requires: ['swamp-venom', 'swamp-camp'],
        giver: ELDER,
        turnIn: SCHOLAR,
        offer: 'Il y a un savant de Havrebourg qui fouille les Ruines Noyées, au sud-est, depuis des années. Il s’appelle Théophane. Il parle beaucoup trop, mais il sait lire ce genre de dessins. Montre-lui les os. Et ne le lance pas sur l’histoire des lézards, tu y passerais la nuit.',
        summary: 'Rejoindre les Ruines Noyées, au sud-est, et parler à l’Érudit Théophane.',
        pending: 'Les ruines sont au sud-est, à moitié sous l’eau. Tu entendras Théophane parler tout seul.',
        complete: 'Des spirales ! Montre, montre ! Trois tours et un point au milieu… C’est l’écriture des anciens hommes-lézards ! J’en ai trouvé quatre cent douze sur ces murs, et personne n’en avait gravé de nouvelle depuis mille ans. Celle-ci a un mois. C’est fascinant ! Et un peu inquiétant.',
        objectives: [
            { kind: 'reach', label: 'Atteindre les Ruines Noyées', landmark: 'Ruines Noyées' },
        ],
        reward: { xp: 250, gold: 50 },
    },
    {
        id: 'swamp-rites',
        name: 'Le chant des ruines',
        biome: 'swamp',
        level: 11,
        requires: ['swamp-ruins'],
        giver: SCHOLAR,
        turnIn: SCHOLAR,
        offer: 'Chaque nuit, les hommes-lézards viennent chanter ici, et les crapauds leur répondent. Et à chaque chanson, la brume monte un peu plus, je l’ai mesuré sur cette colonne ! Va les faire taire : les crapauds et les chanteurs. Moi, je prends des notes.',
        summary: 'Briser le chœur des ruines : abattre crapauds géants et hommes-lézards.',
        pending: 'Continue ! Il manque déjà une voix dans le chœur, la plus grave. Je l’aimais bien, celle-là, remarque.',
        complete: 'La brume est descendue d’un doigt ! C’est énorme, tu te rends compte ? Non ? Bon. Il reste encore celui qui dirige le chant.',
        objectives: [
            { kind: 'kill', label: 'Crapauds du chœur', mobs: ['toad'], count: 18 },
            { kind: 'kill', label: 'Chanteurs lézards', mobs: ['lizardman'], count: 12 },
        ],
        reward: { xp: 750, gold: 150, items: [{ templateId: 'swamp-tonic', quantity: 3 }] },
    },
    {
        id: 'swamp-shaman',
        name: 'Le Chaman lézard',
        biome: 'swamp',
        level: 11,
        requires: ['swamp-rites'],
        giver: SCHOLAR,
        turnIn: SCHOLAR,
        offer: 'Celui qui dirige le chant, c’est un chaman. Il ne se montre qu’à ceux qui lui apportent une idole. J’en ai sculpté une, en m’inspirant des murs. Elle est fausse, mais il n’en a jamais vu de vraie ! Pose-la près d’un repaire d’hommes-lézards. Attention, il est plus fort que les autres.',
        summary: 'Poser l’idole d’écailles près d’un repaire d’hommes-lézards et abattre le Chaman lézard.',
        pending: 'Pose l’idole près d’un repaire d’hommes-lézards, loin du village. S’il se méfie, recommence. Ma sculpture est très réussie, il finira par venir.',
        complete: 'Regarde, c’est écrit sur son casque, en spirales : il chantait pour un troll, dans le Bourbier ! Garde le casque, il te protégera bien. Moi, je garde les spirales.',
        grants: [{ templateId: 'scale-idol', quantity: 1 }],
        objectives: [
            { kind: 'kill', label: 'Chaman lézard abattu', mobs: ['lizard-shaman'], count: 1 },
        ],
        reward: { xp: 600, gold: 120, items: [{ templateId: 'scale-helm-rare', quantity: 1 }] },
    },
    {
        id: 'swamp-summit',
        name: 'Au-dessus de la brume',
        biome: 'swamp',
        level: 11,
        requires: ['swamp-shaman'],
        giver: SCHOLAR,
        turnIn: SCHOLAR,
        offer: 'Les textes parlent d’une colline d’où on voit tout le marais : le Tertre Noyé, au sud du village. On y monte par la grotte. Va regarder d’où vient la brume. Moi, je parie que c’est du nord. Je parie toujours sur le nord, et je gagne toujours.',
        summary: 'Monter au sommet du Tertre Noyé par sa grotte, puis revenir raconter ce qu’on y a vu.',
        pending: 'Entre dans la grotte au pied du rocher. Au fond, une trappe mène au sommet.',
        complete: 'Une colonne de brume au nord, au-dessus du Bourbier ? J’ai gagné ! Donc le troll dort là-bas. Prends ces potions. La Doyenne me les a données, mais moi, je ne mettrai jamais les pieds là-dedans.',
        objectives: [{ kind: 'reach', label: 'Atteindre le Tertre Noyé', landmark: 'Tertre Noyé' }],
        reward: { xp: 350, gold: 70, items: [{ templateId: 'swamp-potion', quantity: 2 }] },
    },
    {
        id: 'swamp-king',
        name: 'Le Roi des Tourbières',
        biome: 'swamp',
        level: 12,
        requires: ['swamp-camp'],
        giver: HUNTER,
        turnIn: HUNTER,
        offer: 'Tu connais le Roi des Tourbières ? Un crapaud vieux comme le marais, avec des nénuphars sur la tête comme une couronne, je te jure ! Je le chasse depuis mes dix ans. Il adore le lotus noir. Pose-le près d’un repaire de crapauds. Et prends des potions, il m’a cassé trois côtes.',
        summary: 'Poser le lotus noir près d’un repaire de crapauds et abattre le Roi des Tourbières.',
        pending: 'Pose le lotus noir près d’un repaire de crapauds. Il met du temps à sortir, il est vieux.',
        complete: 'Tu l’as eu ? Toi ? Bon… Prends ce bouclier, il est taillé dans la carapace d’un de ses ancêtres. Je le gardais pour le jour où je l’aurais moi-même. Tant pis. Mais la prochaine grosse bête, elle est pour moi !',
        grants: [{ templateId: 'black-lotus', quantity: 1 }],
        objectives: [
            { kind: 'kill', label: 'Roi des Tourbières abattu', mobs: ['bog-king'], count: 1 },
        ],
        reward: { xp: 800, gold: 160, items: [{ templateId: 'bog-king-shield', quantity: 1 }] },
    },
    {
        id: 'swamp-muster',
        name: 'Rassembler Brumevase',
        biome: 'swamp',
        level: 12,
        requires: ['swamp-summit'],
        giver: ELDER,
        turnIn: ELDER,
        offer: 'Un troll ! Mon grand-père avait raison, le vieux soûlard. Il va falloir tout le village pour l’approcher. Va voir Ansgar, Guénola et Nella. Ils ne s’entendent pas très bien, alors dis-leur que c’est moi qui demande.',
        summary: 'Rallier le passeur, la fossoyeuse et la traqueuse contre le troll, puis revenir à la Doyenne.',
        pending: 'Ansgar est au nord, Guénola au cimetière et Nella au sud. Et ne leur dis pas qui tu as vu en premier, ils sont jaloux.',
        complete: 'Ils viennent tous les trois ! La dernière fois qu’ils étaient d’accord sur quelque chose, c’était pendant la grande inondation, et j’avais douze ans.',
        objectives: [
            {
                kind: 'talk',
                label: 'Rallier le passeur',
                npc: FERRYMAN,
                line: 'Le Bourbier ? Aucune barque n’y est allée depuis vingt ans. La mienne ira ! Je connais un passage que les caïmans évitent. Ne me demande pas pourquoi, je ne veux pas savoir.',
            },
            {
                kind: 'talk',
                label: 'Rallier la fossoyeuse',
                npc: GRAVEDIGGER,
                line: 'Le Bourbier est derrière mon cimetière, au nord-est. Je garderai le chemin. Et si tu ne reviens pas, je te creuserai un joli trou, à côté du gardien. Il est très calme, tu verras.',
            },
            {
                kind: 'talk',
                label: 'Rallier la traqueuse',
                npc: HUNTER,
                line: 'Un troll ? Enfin un gibier à ma taille ! Je ne descends pas dans son trou, je ne suis pas folle. Mais je nettoie les environs avec toi. Et si tu ne t’en sors pas, je le prends pour moi !',
            },
        ],
        reward: { xp: 300, gold: 60, items: [{ templateId: 'swamp-potion', quantity: 2 }] },
    },
    {
        id: 'swamp-bog',
        name: 'Aux portes du Bourbier',
        biome: 'swamp',
        level: 12,
        requires: ['swamp-muster'],
        giver: ELDER,
        turnIn: ELDER,
        offer: 'Le troll a des gardes : les hommes-lézards qui chantaient pour lui, et les caïmans qu’il nourrit. Va au Bourbier, au nord, et débarrasse-toi d’eux. Comme ça, personne ne t’attaquera par derrière quand tu descendras.',
        summary: 'Rejoindre le Bourbier du Troll et abattre les hommes-lézards et caïmans qui en gardent les abords.',
        pending: 'Le Bourbier est au nord, après le cimetière. Suis le chemin de terre et ne le quitte pas, la vase avale tout.',
        complete: 'La voie est libre ! Prends cette épée. Mon mari voulait descendre dans le Bourbier quand on était jeunes, et je l’en ai empêché. Il m’a boudé pendant quarante ans.',
        objectives: [
            { kind: 'reach', label: 'Atteindre le Bourbier du Troll', landmark: 'Bourbier du Troll' },
            { kind: 'kill', label: 'Gardes lézards abattus', mobs: ['lizardman'], count: 14 },
            { kind: 'kill', label: 'Caïmans du Bourbier', mobs: ['caiman'], count: 10 },
        ],
        reward: { xp: 850, gold: 170, items: [{ templateId: 'scale-sword-rare', quantity: 1 }] },
    },
    {
        id: 'swamp-troll',
        name: 'Le Troll des Tourbières',
        biome: 'swamp',
        level: 12,
        requires: ['swamp-bog'],
        giver: ELDER,
        turnIn: ELDER,
        offer: 'Le Troll des Tourbières dort au fond du Bourbier. C’est son souffle qui fait la brume, d’après Théophane, et pour une fois, je le crois. Descends le battre, et emmène des amis si tu peux. Moi, je t’attends ici, mes genoux ne descendent plus d’escaliers.',
        summary: 'Abattre le Troll des Tourbières au fond de son donjon.',
        pending: 'Le troll est au fond du Bourbier, au nord. Prends des potions, plus que tu ne le penses.',
        complete: 'Regarde, là-haut ! C’est jaune ! C’est le soleil ! Je ne l’avais pas vu depuis mes douze ans ! Prends ce bouclier, il était accroché au-dessus de ma porte depuis la grande inondation. Ah, et Nella est passée ce matin. Elle a trouvé une trace qui ne vient pas du troll. Elle t’attend à la sortie sud.',
        objectives: [
            { kind: 'kill', label: 'Troll des Tourbières abattu', mobs: ['bog-troll'], count: 1 },
        ],
        reward: { xp: 1300, gold: 260, items: [{ templateId: 'scale-shield-rare', quantity: 1 }] },
    },
    {
        id: 'swamp-hatchling',
        name: 'Le nid du Bourbier',
        biome: 'swamp',
        level: 12,
        requires: ['swamp-troll'],
        giver: ELDER,
        turnIn: HUNTER,
        offer: 'Autre chose : Nella a trouvé un nid de caïman au Bourbier, sans maman, avec un œuf qui bouge encore ! Va le chercher avant que les hérons le mangent, et apporte-le à Nella, à la sortie sud.',
        summary: 'Rejoindre le Bourbier du Troll pour y prendre le dernier œuf, puis le porter à la Traqueuse Nella.',
        pending: 'L’œuf est au Bourbier, au nord. Nella attend à la sortie sud. Garde-le bien au chaud !',
        complete: 'Donne ! Chut… Tu entends ? Il toque ! Et voilà, il est sorti ! Oh, il te regarde, toi, pas moi. Tant pis. Un bébé caïman n’oublie jamais le premier visage qu’il voit. Garde-le, il te suivra partout et ramassera ce que tu fais tomber. Il mord un peu, mais c’est affectueux.',
        objectives: [
            { kind: 'reach', label: 'Prendre l’œuf au Bourbier du Troll', landmark: 'Bourbier du Troll' },
        ],
        reward: { xp: 80, gold: 16, items: [{ templateId: 'swamp-caiman', quantity: 1 }] },
    },
    {
        id: 'swamp-south',
        name: 'La piste de Sablerouge',
        biome: 'swamp',
        level: 12,
        requires: ['swamp-recruit'],
        giver: ELDER,
        turnIn: CARAVAN_MASTER,
        offer: 'Nella est partie avec ton chevalier. Je ne pensais jamais la voir quitter le marais ! Théophane dit que ses vieux textes parlent d’un roi du sud qui payait le dragon pour ne pas brûler son royaume. Va voir ce que le désert sait de cette histoire. À Sablerouge, le Maître de caravane cherche toujours des gens courageux. Dis-lui que c’est moi qui t’envoie.',
        summary: 'Suivre la route du sud jusqu’à Sablerouge et parler au Maître de caravane.',
        pending: 'Sablerouge est loin au sud, dans le désert. Emporte de l’eau, beaucoup d’eau !',
        complete: 'C’est la Doyenne qui t’envoie ? Elle me doit encore quarante tonneaux de sel ! Dis-lui que je n’ai pas oublié. Allez, entre à l’ombre, tu es tout rouge.',
        objectives: [{ kind: 'reach', label: 'Atteindre Sablerouge', landmark: 'Sablerouge' }],
        reward: { xp: 300, gold: 60 },
    },
];
//# sourceMappingURL=swamp-depths.js.map